import { useMemo } from "react";
import { useMatchedCandidate, useParticipantDetails, useParticipants } from "./useParticipants";
import { getChannel, getPrimaryParticipant } from "@/components/inbox/utils";
import type { InboxConversation, InboxParticipant } from "@/types/inbox";

/**
 * Everything contextual about the selected conversation's counterpart:
 * participants, the primary (external) participant with detailed profile,
 * and the CRM candidate matched via email / LinkedIn identifier.
 */
export function useConversationContext(conversation: InboxConversation | undefined) {
	const channel = getChannel(conversation);
	const participantsQuery = useParticipants(conversation);
	const participants = useMemo(
		() => participantsQuery.data || conversation?.participants || [],
		[participantsQuery.data, conversation?.participants]
	);

	const primaryParticipant = getPrimaryParticipant(conversation, participants);
	const detailsQuery = useParticipantDetails(
		channel === "email" ? undefined : primaryParticipant?.id
	);

	const candidateLinkKey = useMemo(() => {
		if (detailsQuery.data?.candidateLinkKey) return detailsQuery.data.candidateLinkKey;
		if (channel === "email") return primaryParticipant?.email || null;
		return null;
	}, [detailsQuery.data?.candidateLinkKey, channel, primaryParticipant?.email]);

	const matchedCandidateQuery = useMatchedCandidate(channel, candidateLinkKey);

	const participantsById = useMemo(() => {
		const map: Record<string, InboxParticipant> = {};
		for (const participant of participants) {
			if (participant.id) map[participant.id] = participant;
		}
		return map;
	}, [participants]);

	const profileUrl =
		detailsQuery.data?.profileUrl ||
		participants.find((p) => p.profile_url)?.profile_url ||
		null;

	return {
		channel,
		participants,
		participantsById,
		participantsLoading: participantsQuery.isLoading,
		primaryParticipant,
		participantDetails: detailsQuery.data,
		profileUrl,
		candidateLinkKey,
		matchedCandidate: matchedCandidateQuery.data ?? null,
		matchedCandidateLoading: matchedCandidateQuery.isLoading,
	};
}
